import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  BankStatementsList,
  BankStatementsListAPI,
  CreatePayoutPayload,
  ExportDocument,
  ExportDocumentPayload,
  ExportZipFolderPayload,
  PublicAsfInvoices,
  PublicAsfInvoicesApi,
  PublicDeposits,
  PublicDepositsApi,
  PublicInvoices,
  PublicInvoicesApi,
  PublicStatements,
  PublicStatementsAPI,
  UpdateCompanyType,
} from "./billingsAPI";
import axios from "axios";
import {
  formattedZipFolderFileName,
  fromToDateFormatterDayMonthLongYear,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterLongMonthYear,
} from "@qivia/ui";
import { loadCompanyAsync } from "../homeSlice";

export interface BillingsState {
  statementsListStatus: QueryStatus;
  statementsList: PublicStatements[];
  statementExportStatus: QueryStatus;
  statementExportLink: ExportDocument;
  statementDownloadStatus: QueryStatus;
  updateCompanyStatus: QueryStatus;
  deleteRecipientEmailStatus: QueryStatus;
  invoicesListStatus: QueryStatus;
  invoicesList: PublicInvoices[];
  depositsListStatus: QueryStatus;
  depositsList: PublicDeposits[];
  asfInvoicesListStatus: QueryStatus;
  asfInvoicesList: PublicAsfInvoices[];
  invoiceExportStatus: QueryStatus;
  invoiceDownloadStatus: QueryStatus;
  invoiceExportLink: ExportDocument;
  depositExportStatus: QueryStatus;
  depositExportLink: ExportDocument;
  asfInvoicesExportStatus: QueryStatus;
  asfInvoicesExportLink: ExportDocument;
  bankDetailsExportStatus: QueryStatus;
  bankDetailsExportLink: ExportDocument;
  bankDetailsDownloadStatus: QueryStatus;
  bankStatementExportStatus: QueryStatus;
  bankStatementExportLink: ExportDocument;
  bankStatementsListStatus: QueryStatus;
  bankStatementsList: BankStatementsList[];
  createPayoutStatus: QueryStatus;
  supportingDocumentsZipFolderExportStatus: QueryStatus;
}

const initialState: BillingsState = {
  statementsListStatus: "idle",
  statementsList: [],
  statementExportStatus: "idle",
  statementExportLink: {
    presignedUrl: null,
    fileName: null,
  },
  statementDownloadStatus: "idle",
  updateCompanyStatus: "idle",
  deleteRecipientEmailStatus: "idle",
  invoicesListStatus: "idle",
  invoicesList: [],
  depositsListStatus: "idle",
  depositsList: [],
  asfInvoicesListStatus: "idle",
  asfInvoicesList: [],
  invoiceExportStatus: "idle",
  invoiceDownloadStatus: "idle",
  invoiceExportLink: {
    presignedUrl: null,
    fileName: null,
  },
  depositExportStatus: "idle",
  depositExportLink: {
    presignedUrl: null,
    fileName: null,
  },
  asfInvoicesExportStatus: "idle",
  asfInvoicesExportLink: {
    presignedUrl: null,
    fileName: null,
  },
  bankDetailsExportStatus: "idle",
  bankDetailsExportLink: {
    presignedUrl: null,
    fileName: null,
  },
  bankDetailsDownloadStatus: "idle",
  bankStatementExportStatus: "idle",
  bankStatementExportLink: {
    presignedUrl: null,
    fileName: null,
  },
  bankStatementsListStatus: "idle",
  bankStatementsList: [],
  createPayoutStatus: "idle",
  supportingDocumentsZipFolderExportStatus: "idle",
};

export const statementsListAsync = createAsyncThunk(
  "statementsList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<Array<PublicStatementsAPI>>(
      `/statements/company/${companyUuid}`,
    );
    return response.data.map((d) => {
      return {
        ...d,
        amount: d.amount / 100,
        date: fromToDateFormatterDayMonthLongYear(
          new Date(d.fromDate),
          new Date(d.toDate),
        ),
        zipFolderBucketFileNameDownload: d.zipFolder,
        bucketFileNameDownload: d.bucketFileName,
        invoiceBucketFileNameDownload: d.bucketFileName
          ? `${d.bucketFileName.split(".xlsx")[0] + ".pdf"}`
          : "",
      };
    });
  },
);

export const invoicesListAsync = createAsyncThunk(
  "invoicesList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<PublicInvoicesApi[]>(
      `/invoices/company/${companyUuid}`,
    );
    return response.data
      .map((invoice) => {
        return {
          ...invoice,
          date: dateFormatterDayMonthYearLong2Digits(
            new Date(invoice.createdAt),
          ),
          dueDate: dateFormatterDayMonthYearLong2Digits(
            new Date(invoice.dueDate),
          ),
          fileName: invoice.name,
          totalAmount: invoice.totalAmount / 100,
          bucketFileNameDownload: invoice.bucketFileName,
        };
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  },
);

export const depositsListAsync = createAsyncThunk(
  "depositsList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<PublicDepositsApi[]>(
      `/deposits/company/${companyUuid}`,
    );
    const depositsList = response.data.map((deposit) => {
      return {
        ...deposit,
        date: dateFormatterDayMonthYearLong2Digits(new Date(deposit.date)),
        bucketFileNameDownload: deposit.bucketFileName ?? "",
        createdAt: dateFormatterDayMonthYearLong2Digits(
          new Date(deposit.createdAt),
        ),
        amount: deposit.amount / 100,
      };
    });
    return depositsList.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  },
);

export const asfInvoicesListAsync = createAsyncThunk(
  "asfInvoicesList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<PublicAsfInvoicesApi[]>(
      `/asf_invoices/company/${companyUuid}`,
    );
    const asfInvoicesList = response.data.map((asfInvoice) => {
      return {
        ...asfInvoice,
        beginningDate: dateFormatterDayMonthYearLong2Digits(
          new Date(asfInvoice.beginningDate),
        ),
        endDate: dateFormatterDayMonthYearLong2Digits(
          new Date(asfInvoice.endDate),
        ),
        createdAt: dateFormatterDayMonthYearLong2Digits(
          new Date(asfInvoice.createdAt),
        ),
        transactionsAmountTtc: asfInvoice.transactionsAmountTtc / 100,
        bucketFileNameDownload: asfInvoice.bucketFileName ?? "",
        bucketFileNameDownloadXlsx: asfInvoice.bucketFileNameXlsx ?? "",
      };
    });
    return asfInvoicesList.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  },
);

export const statementExportAsync = createAsyncThunk(
  "statementExport/call",
  async (payload: ExportDocumentPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ presignedUrl: string }>(
      `/statement/${payload.bucketFileName}/export`,
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName: payload.fileName,
    };
  },
);

export const bankStatementsListAsync = createAsyncThunk(
  "bankStatementsList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<BankStatementsListAPI[]>(
      `bank_statements/company/${companyUuid}`,
    );
    const bankStatementsList = response.data.map((d) => {
      return {
        ...d,
        bucketFileNameDownload: d.bucketFileName ?? "",
        bucketFileNameDownloadXlsx: d.bucketFileNameXlsx ?? "",
        closingBalance:
          d.closingBalance === 0
            ? "0"
            : d.closingBalance !== null
              ? d.closingBalance / 100
              : null,
        date: dateFormatterLongMonthYear(new Date(d.date)),
      };
    });
    return bankStatementsList.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  },
);

export const supportingDocumentsZipFolderExportAsync = createAsyncThunk(
  "supportingDocumentsZipFolderExport/call",
  async (payload: ExportZipFolderPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.post<{ presignedUrl: string }>(
      `/statement/zip_folder/${payload.zipFolderUuid}/link`,
    );
    const fileName = formattedZipFolderFileName(
      payload.companyName,
      new Date(payload.date),
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName,
    };
  },
);

export const invoiceExportAsync = createAsyncThunk(
  "invoiceExport/call",
  async (payload: ExportDocumentPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ presignedUrl: string }>(
      `/invoice/${payload.bucketFileName}/export`,
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName: payload.fileName,
    };
  },
);

export const depositExportAsync = createAsyncThunk(
  "depositExport/call",
  async (payload: ExportDocumentPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ presignedUrl: string }>(
      `/deposit/${payload.fileName}/export`,
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName: payload.fileName,
    };
  },
);

export const asfInvoicesExportAsync = createAsyncThunk(
  "asfInvoicesExport/call",
  async (payload: ExportDocumentPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ presignedUrl: string }>(
      `/asf_invoices/${payload.bucketFileName}/export`,
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName: payload.fileName,
    };
  },
);

export const bankDetailsExportAsync = createAsyncThunk(
  "bankDetailsExport/call",
  async (payload: ExportDocumentPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ presignedUrl: string }>(
      `/company/bank_details/${payload.bucketFileName}/export`,
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName: payload.fileName,
    };
  },
);

export const bankStatementExportAsync = createAsyncThunk(
  "bankStatementExport/call",
  async (payload: ExportDocumentPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ presignedUrl: string }>(
      `/bank_statement/${payload.bucketFileName}/export`,
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName: payload.fileName,
    };
  },
);

export const documentDownloadAsync = createAsyncThunk(
  "invoiceDownload/call",
  async (docExportLink: ExportDocument) => {
    if (docExportLink.presignedUrl && docExportLink.fileName) {
      const response = await axios({
        url: docExportLink.presignedUrl,
        method: "GET",
        responseType: "blob",
      });

      const a = document.createElement("a");
      a.download = docExportLink.fileName;
      const url = URL.createObjectURL(new Blob([response.data]));
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);
    }
  },
);

export const updateRecipientEmailAsync = createAsyncThunk(
  "companies/update_recipients_emails/call",
  async (payload: UpdateCompanyType, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/companies/update_recipients_emails`, payload);
  },
);

export const deleteRecipientEmailAsync = createAsyncThunk(
  "companies/update_recipients_emails/delete",
  async (payload: UpdateCompanyType, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/companies/update_recipients_emails`, payload);
  },
);

export const createPayoutAsync = createAsyncThunk(
  "createPayout/call",
  async (payload: CreatePayoutPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/payouts`, payload);
    await new Promise((res) => setTimeout(res, 1500));
    if (company) {
      void thunkAPI.dispatch(loadCompanyAsync({ uuid: company.uuid }));
    }
  },
);

export const billingsSlice = createSlice({
  name: "billings",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetStatementsListStatus(state) {
      state.statementsListStatus = "idle";
    },
    resetBankStatementsListStatus(state) {
      state.bankStatementsListStatus = "idle";
    },
    resetDepositsListStatus(state) {
      state.depositsListStatus = "idle";
    },
    resetAsfInvoicesListStatus(state) {
      state.asfInvoicesListStatus = "idle";
    },
    resetInvoicesListStatus(state) {
      state.invoicesListStatus = "idle";
    },
    resetStatementsExportStatus(state) {
      state.statementExportStatus = "idle";
    },
    resetBankDetailsExportStatus(state) {
      state.bankDetailsExportStatus = "idle";
    },
    resetUpdateCompanyStatus(state) {
      state.updateCompanyStatus = "idle";
    },
    resetDeleteRecipientEmailStatus(state) {
      state.deleteRecipientEmailStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(statementsListAsync.pending, (state) => {
        state.statementsListStatus = "processing";
      })
      .addCase(statementsListAsync.fulfilled, (state, action) => {
        state.statementsList = action.payload;
        state.statementsListStatus = "success";
      })
      .addCase(statementsListAsync.rejected, (state) => {
        state.statementsListStatus = "failed";
      })
      .addCase(statementExportAsync.pending, (state) => {
        state.statementExportStatus = "processing";
      })
      .addCase(statementExportAsync.fulfilled, (state, action) => {
        state.statementExportLink = action.payload;
        state.statementExportStatus = "success";
      })
      .addCase(statementExportAsync.rejected, (state) => {
        state.statementExportStatus = "failed";
      })
      .addCase(supportingDocumentsZipFolderExportAsync.pending, (state) => {
        state.supportingDocumentsZipFolderExportStatus = "processing";
      })
      .addCase(
        supportingDocumentsZipFolderExportAsync.fulfilled,
        (state, action) => {
          state.supportingDocumentsZipFolderExportStatus = "success";
          state.statementExportLink = action.payload;
        },
      )
      .addCase(supportingDocumentsZipFolderExportAsync.rejected, (state) => {
        state.supportingDocumentsZipFolderExportStatus = "failed";
      })
      .addCase(documentDownloadAsync.pending, (state) => {
        state.statementDownloadStatus = "processing";
      })
      .addCase(documentDownloadAsync.fulfilled, (state) => {
        state.statementExportLink.presignedUrl = null;
        state.statementExportLink.fileName = null;
        state.invoiceExportLink.presignedUrl = null;
        state.invoiceExportLink.fileName = null;
        state.bankDetailsExportLink.presignedUrl = null;
        state.bankDetailsExportLink.fileName = null;
        state.bankStatementExportLink.presignedUrl = null;
        state.bankStatementExportLink.fileName = null;
        state.depositExportLink.presignedUrl = null;
        state.depositExportLink.fileName = null;
        state.asfInvoicesExportLink.presignedUrl = null;
        state.asfInvoicesExportLink.fileName = null;
        state.supportingDocumentsZipFolderExportStatus = "idle";
        state.bankStatementExportStatus = "idle";
        state.invoiceExportStatus = "idle";
        state.depositExportStatus = "idle";
        state.asfInvoicesExportStatus = "idle";
        state.statementDownloadStatus = "success";
      })
      .addCase(documentDownloadAsync.rejected, (state) => {
        state.statementDownloadStatus = "failed";
      })
      .addCase(updateRecipientEmailAsync.pending, (state) => {
        state.updateCompanyStatus = "processing";
      })
      .addCase(updateRecipientEmailAsync.fulfilled, (state) => {
        state.updateCompanyStatus = "success";
      })
      .addCase(updateRecipientEmailAsync.rejected, (state) => {
        state.updateCompanyStatus = "failed";
      })
      .addCase(deleteRecipientEmailAsync.pending, (state) => {
        state.deleteRecipientEmailStatus = "processing";
      })
      .addCase(deleteRecipientEmailAsync.fulfilled, (state) => {
        state.deleteRecipientEmailStatus = "success";
      })
      .addCase(deleteRecipientEmailAsync.rejected, (state) => {
        state.deleteRecipientEmailStatus = "failed";
      })
      .addCase(invoicesListAsync.pending, (state) => {
        state.invoicesListStatus = "processing";
      })
      .addCase(invoicesListAsync.fulfilled, (state, action) => {
        state.invoicesList = action.payload;
        state.invoicesListStatus = "success";
      })
      .addCase(invoicesListAsync.rejected, (state) => {
        state.invoicesListStatus = "failed";
      })
      .addCase(depositsListAsync.pending, (state) => {
        state.depositsListStatus = "processing";
      })
      .addCase(depositsListAsync.fulfilled, (state, action) => {
        state.depositsList = action.payload;
        state.depositsListStatus = "success";
      })
      .addCase(depositsListAsync.rejected, (state) => {
        state.depositsListStatus = "failed";
      })
      .addCase(asfInvoicesListAsync.pending, (state) => {
        state.asfInvoicesListStatus = "processing";
      })
      .addCase(asfInvoicesListAsync.fulfilled, (state, action) => {
        state.asfInvoicesList = action.payload;
        state.asfInvoicesListStatus = "success";
      })
      .addCase(asfInvoicesListAsync.rejected, (state) => {
        state.asfInvoicesListStatus = "failed";
      })
      .addCase(invoiceExportAsync.pending, (state) => {
        state.invoiceExportStatus = "processing";
      })
      .addCase(invoiceExportAsync.fulfilled, (state, action) => {
        state.invoiceExportLink = action.payload;
        state.invoiceExportStatus = "success";
      })
      .addCase(invoiceExportAsync.rejected, (state) => {
        state.invoiceExportStatus = "failed";
      })
      .addCase(depositExportAsync.pending, (state) => {
        state.depositExportStatus = "processing";
      })
      .addCase(depositExportAsync.fulfilled, (state, action) => {
        state.depositExportLink = action.payload;
        state.depositExportStatus = "success";
      })
      .addCase(depositExportAsync.rejected, (state) => {
        state.depositExportStatus = "failed";
      })
      .addCase(asfInvoicesExportAsync.pending, (state) => {
        state.asfInvoicesExportStatus = "processing";
      })
      .addCase(asfInvoicesExportAsync.fulfilled, (state, action) => {
        state.asfInvoicesExportLink = action.payload;
        state.asfInvoicesExportStatus = "success";
      })
      .addCase(asfInvoicesExportAsync.rejected, (state) => {
        state.asfInvoicesExportStatus = "failed";
      })
      .addCase(bankDetailsExportAsync.pending, (state) => {
        state.bankDetailsExportStatus = "processing";
      })
      .addCase(bankDetailsExportAsync.fulfilled, (state, action) => {
        state.bankDetailsExportLink = action.payload;
        state.bankDetailsExportStatus = "success";
      })
      .addCase(bankDetailsExportAsync.rejected, (state) => {
        state.bankDetailsExportStatus = "failed";
      })
      .addCase(bankStatementsListAsync.pending, (state) => {
        state.bankStatementsListStatus = "processing";
      })
      .addCase(bankStatementsListAsync.fulfilled, (state, action) => {
        state.bankStatementsList = action.payload;
        state.bankStatementsListStatus = "success";
      })
      .addCase(bankStatementsListAsync.rejected, (state) => {
        state.bankStatementsListStatus = "failed";
      })
      .addCase(bankStatementExportAsync.pending, (state) => {
        state.bankStatementExportStatus = "processing";
      })
      .addCase(bankStatementExportAsync.fulfilled, (state, action) => {
        state.bankStatementExportLink = action.payload;
        state.bankStatementExportStatus = "success";
      })
      .addCase(bankStatementExportAsync.rejected, (state) => {
        state.bankStatementExportStatus = "failed";
      })
      .addCase(createPayoutAsync.pending, (state) => {
        state.createPayoutStatus = "processing";
      })
      .addCase(createPayoutAsync.fulfilled, (state) => {
        state.createPayoutStatus = "success";
      })
      .addCase(createPayoutAsync.rejected, (state) => {
        state.createPayoutStatus = "failed";
      });
  },
});

export const selectStatementsList = (state: RootState) =>
  state.billings.statementsList;
export const selectStatementsListStatus = (state: RootState) =>
  state.billings.statementsListStatus;
export const selectStatementExportStatus = (state: RootState) =>
  state.billings.statementExportStatus;
export const selectStatementExportLink = (state: RootState) =>
  state.billings.statementExportLink;
export const selectStatementDownloadStatus = (state: RootState) =>
  state.billings.statementDownloadStatus;
export const selectUpdateCompanyStatus = (state: RootState) =>
  state.billings.updateCompanyStatus;
export const selectDeleteRecipientEmailStatus = (state: RootState) =>
  state.billings.deleteRecipientEmailStatus;
export const selectInvoicesList = (state: RootState) =>
  state.billings.invoicesList;
export const selectInvoicesListStatus = (state: RootState) =>
  state.billings.invoicesListStatus;
export const selectDepositsList = (state: RootState) =>
  state.billings.depositsList;
export const selectDepositsListStatus = (state: RootState) =>
  state.billings.depositsListStatus;
export const selectAsfInvoicesList = (state: RootState) =>
  state.billings.asfInvoicesList;
export const selectAsfInvoicesListStatus = (state: RootState) =>
  state.billings.asfInvoicesListStatus;
export const selectInvoiceExportLink = (state: RootState) =>
  state.billings.invoiceExportLink;
export const selectInvoiceExportStatus = (state: RootState) =>
  state.billings.invoiceExportStatus;
export const selectDepositExportLink = (state: RootState) =>
  state.billings.depositExportLink;
export const selectDepositExportStatus = (state: RootState) =>
  state.billings.depositExportStatus;
export const selecAsfInvoicesExportLink = (state: RootState) =>
  state.billings.asfInvoicesExportLink;
export const selectAsfInvoicesExportStatus = (state: RootState) =>
  state.billings.asfInvoicesExportStatus;
export const selectBankDetailsExportStatus = (state: RootState) =>
  state.billings.bankDetailsExportStatus;
export const selectBankDetailsExportLink = (state: RootState) =>
  state.billings.bankDetailsExportLink;
export const selectBankStatementExportStatus = (state: RootState) =>
  state.billings.bankStatementExportStatus;
export const selectBankStatementExportLink = (state: RootState) =>
  state.billings.bankStatementExportLink;
export const selectBankStatementsList = (state: RootState) =>
  state.billings.bankStatementsList;
export const selectBankStatementsListStatus = (state: RootState) =>
  state.billings.bankStatementsListStatus;
export const selectCreatePayoutStatus = (state: RootState) =>
  state.billings.createPayoutStatus;
export const selectSupportingDocumentsZipFolderExportStatus = (
  state: RootState,
) => state.billings.supportingDocumentsZipFolderExportStatus;
export default billingsSlice.reducer;
